import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { registerToken } from '../../../../../portal/libs/maps/providers/esri/identityManager/registerToken';
import { mapLayerSettingsSelector } from '../capstone/psapInfo/psapInfoSlice';
import { getGISServices } from './gisServicesAPI';

/**
 * Handles toggling basemap services (service_type === 0)
 * @param {Object} state - Redux state
 * @param {Object} layer - The layer being toggled
 * @param {boolean} isSublayer - Whether this is a sublayer
 * @param {boolean} [visible] - What state to toggle the layer to
 * @returns {boolean} visible - Whether the layer is now visible
 */
const handleBasemapServiceToggle = (
  state,
  layer,
  isSublayer,
  visible = undefined,
) => {
  if (isSublayer) {
    // sublayer
    const setToVisible =
      visible !== undefined
        ? visible
        : !state.activeBasemapServiceSublayers.find(
            sublayer => sublayer.sublayerID === layer.sublayerID,
          );

    // Remove the layer to start, we'll add it back if it's needed.
    state.activeBasemapServiceSublayers =
      state.activeBasemapServiceSublayers.filter(
        sublayer => sublayer.sublayerID !== layer.sublayerID,
      );
    if (!setToVisible) {
      // if state.activeBasemapServiceSublayers is empty, remove the parent service from state.activeGISServices
      if (state.activeBasemapServiceSublayers.length === 0) {
        const parentServiceId = state.basemapService?.id;
        if (parentServiceId) {
          state.activeGISServices = state.activeGISServices.filter(
            activeService => activeService.id !== parentServiceId,
          );
        }
      }

      return false;
    } else {
      // Show the layer
      state.activeBasemapServiceSublayers.push(layer);

      // if state.activeGISServices does not include the parent service, add that basemapService to the state.activeGISServices
      const parentServiceId = state.basemapService?.id;
      if (parentServiceId) {
        const isParentServiceActive = state.activeGISServices.some(
          activeService => activeService.id === parentServiceId,
        );

        if (!isParentServiceActive) {
          const parentServiceWithSublayers = {
            ...state.basemapService,
            sublayers: state.basemapServiceSublayers,
          };
          state.activeGISServices.push(parentServiceWithSublayers);
        }
      }

      return true;
    }
  } else {
    // main layer
    const setToVisible =
      visible !== undefined
        ? visible
        : !state.activeGISServices.find(service => service.id === layer.id);

    // Remove the layer to start; we'll add it back if that's what's being
    // requested.
    state.activeGISServices = state.activeGISServices.filter(
      service => service.id !== layer.id,
    );
    if (!setToVisible) {
      // Hide the sublayers
      state.activeBasemapServiceSublayers = [];
      return false;
    } else {
      // Show the layer
      state.activeGISServices.push(layer);
      state.activeBasemapServiceSublayers = state.basemapServiceSublayers;
      return true;
    }
  }
};

/**
 * Handles toggling supplemental services (service_type !== 0)
 * @param {Object} state - Redux state
 * @param {Object} layer - The layer being toggled
 * @param {boolean} isSublayer - Whether this is a sublayer
 * @param {boolean} [visible] - What state to toggle the layer to
 * @returns {boolean} visible - Whether the layer is now visible
 */
const handleSupplementalServiceToggle = (
  state,
  layer,
  isSublayer,
  visible = undefined,
) => {
  if (isSublayer) {
    const setToVisible =
      visible !== undefined
        ? visible
        : !state.activeSupplementalServicesSublayers.find(
            sublayer => sublayer.sublayerID === layer.sublayerID,
          );

    // Clear it back
    state.activeSupplementalServicesSublayers =
      state.activeSupplementalServicesSublayers.filter(
        sublayer => sublayer.sublayerID !== layer.sublayerID,
      );
    if (setToVisible) {
      // restore it if we want it present
      state.activeSupplementalServicesSublayers.push(layer);
    }

    const parentServiceId = layer.id;

    const parentServiceSublayers = state.supplementalServicesSublayers.filter(
      sublayer => sublayer.id === parentServiceId,
    );

    const parentService = state.gisServices.find(
      service => service.id === parentServiceId,
    );

    if (parentService && parentServiceSublayers.length > 0) {
      const parentSublayerIds = parentServiceSublayers.map(
        sub => sub.sublayerID,
      );

      const activeSublayersForThisParent =
        state.activeSupplementalServicesSublayers.filter(activeSublayer =>
          parentSublayerIds.includes(activeSublayer.sublayerID),
        );

      const isParentServiceActive = state.activeGISServices.some(
        activeService => activeService.id === parentServiceId,
      );

      if (activeSublayersForThisParent.length > 0) {
        if (!isParentServiceActive) {
          const parentServiceWithSublayers = {
            ...parentService,
            sublayers: parentServiceSublayers,
          };
          state.activeGISServices.push(parentServiceWithSublayers);
        }
      } else if (activeSublayersForThisParent.length === 0) {
        if (isParentServiceActive) {
          state.activeGISServices = state.activeGISServices.filter(
            activeService => activeService.id !== parentServiceId,
          );
        }
      }
    }

    return setToVisible;
  } else {
    // main layer
    const parentService = layer;
    const setToVisible =
      visible !== undefined
        ? visible
        : !state.activeGISServices.find(
            service => service.id === parentService.id,
          );

    // Remove the layer to start
    state.activeGISServices = state.activeGISServices.filter(
      service => service.id !== parentService.id,
    );
    if (!setToVisible) {
      if (parentService.sublayers && parentService.sublayers.length > 0) {
        const sublayerIds = parentService.sublayers.map(
          sublayer => sublayer.sublayerID,
        );
        state.activeSupplementalServicesSublayers =
          state.activeSupplementalServicesSublayers.filter(
            sublayer => !sublayerIds.includes(sublayer.sublayerID),
          );
      }
      return false;
    } else {
      // Show the layer
      state.activeGISServices.push(parentService);

      if (parentService.sublayers && parentService.sublayers.length > 0) {
        state.activeSupplementalServicesSublayers = [
          ...state.activeSupplementalServicesSublayers,
          ...parentService.sublayers,
        ];
      }
      return true;
    }
  }
};

export const fetchGISServices = createAsyncThunk(
  'gisServices/fetchGISServices',
  async (orgName, { rejectWithValue, getState }) => {
    try {
      const response = await getGISServices(orgName);
      const data = response.data;

      // PSAPs can choose to enable layers by default (see
      // /admin/data-source/map-layers). If a layer should be on by default, set
      // its auto_checked here, and it will be turned on after the layers are
      // loaded into state.
      const settings = mapLayerSettingsSelector(getState());
      data.gis_identities.forEach(gisID => {
        gisID.gis_services.forEach(layer => {
          if (settings[layer.id]) {
            layer.auto_checked ||= settings[layer.id].default_checked;
          }
        });
      });

      return data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue({
        message,
        status: error.response?.status,
      });
    }
  },
);

// A mapping of the GIS service types
export const gisServiceTypes = {
  basemap: 0,
  supplemental: 1,
  geocode: 2,
  wms: 3,
  wmts: 4,
};

export const initialState = {
  activeGISServices: [],
  basemapService: {},
  basemapServiceSublayers: [],
  activeBasemapServiceSublayers: [],
  error: null,
  geocodeService: {},
  gisIdentities: [],
  gisServices: [],
  lastToggledGISService: {},
  loading: false,
  numClearAllClicked: 0,
  supplementalServices: [],
  supplementalServicesSublayers: [],
  activeSupplementalServicesSublayers: [],
  wmsServices: [],
  wmtsServices: [],
};

/**
 * Finds the GIS basemap service from a list of GIS services
 * @param {Array} gisServices - list of GIS services
 * @returns Object
 */
export const findGISBasemapService = gisServices =>
  gisServices.find(
    gisService => gisService.service_type === gisServiceTypes.basemap,
  ) || initialState.basemapService;

/**
 * Filters the GIS supplemental services from a list of GIS services
 * @param {array} gisServices - list of GIS services
 * @returns Array
 */
export const filterGISSupplementalService = gisServices =>
  gisServices.filter(
    gisService => gisService.service_type === gisServiceTypes.supplemental,
  );

/**
 * Finds the GIS geocode service from a list of GIS services
 * @param {Array} gisServices - list of GIS services
 * @returns Object
 */
export const findGISGeocodeService = gisServices =>
  gisServices.find(
    gisService => gisService.service_type === gisServiceTypes.geocode,
  ) || initialState.geocodeService;

/**
 * Filters the GIS services from a list of GIS services
 * @param {Array} gisServices - list of GIS services
 * @param {String} serviceType - GIS service type, value should be wms or wmts
 * @returns Array
 */
export const filterServicesByType = (gisServices, serviceType) =>
  gisServices.filter(
    gisService => gisService.service_type === gisServiceTypes[serviceType],
  );

/**
 * Map the GIS identities from the `/gis-services` payload
 * @param {Array} gisIdentities - list of GIS identities
 */
export const mapGISIdentities = gisIdentities =>
  gisIdentities.map(identity => {
    const {
      arcgis_token: arcGISToken,
      gis_identity_url: gisIdentityUrl,
      gis_services: gisServices,
      token_expires_in: tokenExpiresIn,
    } = identity;

    return {
      arcGISToken,
      gisServices,
      gisIdentityUrl,
      tokenExpiresIn,
    };
  });

/**
 * Map the GIS services to a flat sorted array based on the display_order property from the
 * /gis-services payload.
 * @param {Array} gisIdentities - list of GIS identities
 */
export const mapGISServices = gisIdentities => {
  let gisServices = [];
  let layerIndex = 1;

  gisServices = gisIdentities
    .map(identity => {
      return identity.gis_services.map(service => {
        // Update GIS service display_order that has null value to 0 so that it can be sorted later.
        return {
          ...service,
          ...(service.display_order === null && { display_order: 0 }),
          token: identity?.arcgis_token || null,
        };
      });
    })
    .flat()
    .sort((a, b) => {
      // If the compared GIS services have the same display_order value, then sort by service_type.
      if (a.display_order === b.display_order) {
        return a.service_type - b.service_type;
      }

      return a.display_order - b.display_order;
    })
    .map(service => {
      // Add a new layerIndex property so that the layer can be loaded in a specific order.
      layerIndex = !service.display_order ? 1 : (layerIndex += 1);

      return {
        ...service,
        layerIndex,
      };
    });

  return gisServices;
};

/**
 * Filters the active GIS services when a match is found from current GIS services.
 * @param {Array} activeServices - List of active GIS services
 * @param {Array} currentServices - list of GIS Services returned from API request
 * @returns Array
 */
export const filterActiveGISServices = (activeServices, currentServices) =>
  activeServices.filter(active =>
    currentServices.find(current => active.id === current.id),
  );

const gisLayersWithSublayers = state => {
  let processedSupplementalServices = [...(state.gisServices || [])];
  const supplementalServicesSublayers =
    state.supplementalServicesSublayers || [];
  const basemapServiceSublayers = state.basemapServiceSublayers || [];
  if (supplementalServicesSublayers.length) {
    const serviceSublayersMap = new Map();
    let currentSublayers = [];
    let currentServiceId = null;

    supplementalServicesSublayers.forEach(sublayer => {
      if (currentServiceId === null) {
        currentServiceId = sublayer.id;
        currentSublayers = [sublayer];
      } else if (sublayer.id === currentServiceId) {
        currentSublayers.push(sublayer);
      } else {
        serviceSublayersMap.set(currentServiceId, currentSublayers);
        currentServiceId = sublayer.id;
        currentSublayers = [sublayer];
      }
    });

    if (currentServiceId !== null) {
      serviceSublayersMap.set(currentServiceId, currentSublayers);
    }

    processedSupplementalServices = processedSupplementalServices.map(
      service => {
        const sublayers = serviceSublayersMap.get(service.id);
        if (sublayers) {
          return {
            ...service,
            sublayers: sublayers,
          };
        }
        return service;
      },
    );
  }

  // Process basemap service with sublayers
  const processedBasemapServices = [];

  if (
    basemapServiceSublayers.length &&
    Object.keys(state.basemapService).length
  ) {
    processedBasemapServices.push({
      ...state.basemapService,
      sublayers: basemapServiceSublayers,
    });
  }

  return [...processedBasemapServices, ...processedSupplementalServices];
};

const autoToggleGisServices = state => {
  gisLayersWithSublayers(state).forEach(layer => {
    if (!layer.auto_checked) return;
    if (layer.service_type === 0) {
      // basemap service
      handleBasemapServiceToggle(state, layer, false, true);
    } else {
      // supplemental service
      handleSupplementalServiceToggle(state, layer, false, true);
    }
  });
};

const gisServicesSlice = createSlice({
  name: 'gisServices',
  initialState,
  reducers: {
    toggleGISService: (state, action) => {
      const service = action.payload;
      const isSublayer = service.isSublayer;
      const layer = service.layer;

      if (!layer) {
        state.lastToggledGISService = service;
        state.activeGISServices = [];
        state.activeSupplementalServicesSublayers = [];
        return state;
      }

      let visible;
      if (layer.service_type === 0) {
        // basemap service
        visible = handleBasemapServiceToggle(state, layer, isSublayer);
      } else {
        // supplemental service
        visible = handleSupplementalServiceToggle(state, layer, isSublayer);
      }

      state.lastToggledGISService = {
        ...service,
        visible,
      };
    },
    clearAllGISServices: state => {
      state.activeGISServices = initialState.activeGISServices;
      state.lastToggledGISService = initialState.lastToggledGISService;
      state.activeSupplementalServicesSublayers =
        initialState.activeSupplementalServicesSublayers;
      state.numClearAllClicked += 1;
    },
    addSupplementalServicesSublayers: (state, action) => {
      state.supplementalServicesSublayers = action.payload;
      autoToggleGisServices(state);
    },
    addBasemapServiceSublayers: (state, action) => {
      state.basemapServiceSublayers = action.payload;
      state.activeBasemapServiceSublayers = action.payload;
      autoToggleGisServices(state);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGISServices.pending, state => {
        state.loading = true;
      })
      .addCase(fetchGISServices.fulfilled, (state, action) => {
        state.error = initialState.error;
        state.loading = initialState.loading;

        const gisIdentities = action.payload?.gis_identities || [];

        if (gisIdentities.length) {
          const identities = mapGISIdentities(gisIdentities);

          state.gisIdentities = identities;

          const services = mapGISServices(gisIdentities);

          if (services.length) {
            state.gisServices = services;
            const basemapService = findGISBasemapService(services);
            if (Object.keys(basemapService).length > 0) {
              // if the basemap service add it to the activeGISServices
              state.activeGISServices = [basemapService];
            }
            state.basemapService = basemapService;
            registerToken(basemapService.gis_service_url, basemapService.token);
            state.supplementalServices = filterServicesByType(
              services,
              'supplemental',
            );

            state.geocodeService = findGISGeocodeService(services);
            state.wmsServices = filterServicesByType(services, 'wms');
            state.wmtsServices = filterServicesByType(services, 'wmts');

            autoToggleGisServices(state);
          }
        }
      })
      .addCase(fetchGISServices.rejected, (state, action) => {
        if (action.payload) {
          const { message = '', status = null } = action.payload;

          state = {
            ...initialState,
            error: {
              message: message,
              status: status,
            },
          };

          return state;
        }
      });
  },
});

export const {
  toggleGISService,
  clearAllGISServices,
  addSupplementalServicesSublayers,
  addBasemapServiceSublayers,
} = gisServicesSlice.actions;

// Note: Use of blacklist in both store.js and here results in all keys except
// the blacklisted key here being persisted.
const persistConfig = {
  key: 'gisServices',
  storage,
  blacklist: [
    'lastToggledGISService',
    'numClearAllClicked',
    'activeSupplementalServicesSublayers',
    'activeBasemapServiceSublayers',
  ], // Do not persist on page refresh
};

const persistedReducer = persistReducer(
  persistConfig,
  gisServicesSlice.reducer,
);

export default persistedReducer;
